import { useUserEventLogger } from '@repo/tracking-service';
import cn from 'clsx';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { PreloadedQuery, usePreloadedQuery } from 'react-relay';

import { businessSameIndustryQuery, YesNoBlank } from '@/__generated__/businessSameIndustryQuery.graphql';
import { SurveyTopNavigation } from '@/components/survey/common/TopNavigation';
import { useSurveyUserEventAttributes } from '@/components/survey/common/hooks/use-survey-user-event-attributes';
import { AnswerList, CTA, ProgressBar } from '@/components/survey/common/ui';
import common from '@/components/survey/common/ui/styles/common.module.scss';
import { upsertSameIndustry } from '@/graphql/mutation/survey/business';
import { sameIndustryQuery } from '@/graphql/query/survey/business';
import { SURVEY_PAGES } from '@/lib/constants/paths';
import { useUpsertBusinessAnswer } from '@/lib/hooks/api/mutation/survey/use-upsert-business-answer';

const answers: { value: YesNoBlank; label: string }[] = [
  { value: 'yes', label: '네' },
  { value: 'no', label: '아니요' }
];

export const SameIndustryBeforeContainer = ({ queryRef }: { queryRef: PreloadedQuery<businessSameIndustryQuery> }) => {
  const { sendClickEvent } = useUserEventLogger();
  const eventAttributes = useSurveyUserEventAttributes();
  const router = useRouter();
  const { getSurveyBusinessAnswer } = usePreloadedQuery<businessSameIndustryQuery>(sameIndustryQuery, queryRef);
  const [upsertAnswer, isLoading] = useUpsertBusinessAnswer(upsertSameIndustry);

  const [selectedAnswer, setSelectedAnswer] = useState<YesNoBlank>('blank');

  useEffect(() => {
    const prevAnswer = getSurveyBusinessAnswer?.businessAnswer?.[0]?.hasPreviousBusinessInSameIndustry || 'blank';
    if (prevAnswer) {
      setSelectedAnswer(prevAnswer);
    }
  }, [getSurveyBusinessAnswer?.businessAnswer]);

  return (
    <>
      <SurveyTopNavigation leftClickEvent={'survey_same-industry-before_back'} />
      <main className={cn(common.main_layout, common.main_cta_space)}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            sendClickEvent('survey_same-industry-before_confirm', eventAttributes);

            upsertAnswer(
              { hasPreviousBusinessInSameIndustry: selectedAnswer },
              {
                onCompleted: () => {
                  router.push(SURVEY_PAGES.소사장제지입사);
                }
              }
            );
          }}
        >
          <article>
            <ProgressBar step={1} />

            <section>
              <h1 className={common.content_title}>
                이 사업 창업 전에, 같은 업종으로
                <br />
                사업을 했던 경험이 있나요?
                <span className={'block mt-3 text-16 font-normal leading-[23px] text-gray-700'}>
                  혼자 창업한 경우뿐 아니라, 공동사업자로 운영했던 경험도 포함해서 답변해주세요.
                </span>
              </h1>

              <AnswerList
                answers={answers}
                checkedValue={selectedAnswer}
                onChange={(value) => {
                  sendClickEvent(`survey_same-industry-before_${value}`, eventAttributes);

                  setSelectedAnswer(value);
                }}
              />
            </section>
          </article>

          {selectedAnswer !== 'blank' && (
            <CTA type={'submit'} disabled={isLoading}>
              다음
            </CTA>
          )}
        </form>
      </main>
    </>
  );
};
